import { PersonRounded } from "@mui/icons-material";
import { Grid, Grow, Paper, Typography } from "@mui/material";
import { Container } from "@mui/system";
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button, Input, InputPass } from "../../controls";
import { Model, UseForm } from "../../hooks";
import { Form } from "../../layout";
import { clearSessionScopedCache, ToFormData } from "../../hooks/Method";
import { API, action, controller } from "../../api/api";
import { useAppContext } from "context";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "../body/LanguageSwitcher";
import TwoFactorVerify from "./twoFactor/TwoFactorVerify";
import TwoFactorEnroll from "./twoFactor/TwoFactorEnroll";

const Login = () => {
  const { LoginMdl } = Model();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const { setAuthUser } = useAppContext();
  const { t, i18n } = useTranslation(["common", "validation"]);
  const { values, errors, setErrors, handleInpChg } = UseForm(LoginMdl);
  const [errMsg, setErrMsg] = useState("");

  // A 202 from the password step means the credentials were right but the sign-in
  // is not finished. No session cookie exists yet at this point.
  const [challenge, setChallenge] = useState(null);
  const [enrollment, setEnrollment] = useState(null);

  const validateForm = () => {
    let temp = {};
    temp.username = values.username !== "" ? "" : t("validation:usernameRequired");
    temp.password = values.password !== "" ? "" : t("validation:passwordRequired");
    setErrors({ ...temp });
    return Object.values(temp).every((x) => x === "");
  };

  // Shared by the password step and both two-factor steps — whichever one finally
  // gets a 200 back lands the user in the app the same way.
  const completeSignIn = (user) => {
    if (user?.defaultLangCode) {
      i18n.changeLanguage(user.defaultLangCode);
    }
    setAuthUser(user);
    // Land on "/" — the root route resolves the user's first accessible
    // page once settings/access load (handles users without dashboard).
    navigate("/");
  };

  const restart = () => {
    setChallenge(null);
    setEnrollment(null);
    setErrMsg("");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      setLoading(true);
      const onComplete = (res) => {
        // Belt to logout's brace: a session that ended by closing the tab never
        // ran a logout, so the previous cashier's till would otherwise still be
        // cached when the next one signs in at the same terminal.
        clearSessionScopedCache();

        if (res) {
          switch (res.status) {
            case 403:
              setErrors({
                ...errors,
                [res.error.key]: res.error.msg,
              });
              break;
            case 401:
              setErrMsg(res.msg);
              break;
            // Password accepted, second factor still owed.
            case 202:
              if (res.type === "2fa_enroll") setEnrollment(res.result);
              else setChallenge(res.result);
              break;
            // Locked out (429), blocked after too many wrong attempts until an
            // administrator reactivates the account (423), or the code could not be
            // emailed (503). All fail closed — no session is issued.
            case 423:
            case 429:
            case 503:
              setErrMsg(res.msg);
              break;
            case 200:
              completeSignIn(res.result);
              break;
            default:
              break;
          }
        }
        setLoading(false);
      };
      API(controller.Auth + action.Login).post(
        ToFormData({ ...values, lastVisit: new Date().toISOString() }),
        onComplete,
      );
    }
  };

  return (
    <Grow in timeout={1000}>
      <Container maxWidth="xl">
        <div className="d-flex j-center a-center h-100vh">
          <div style={{ maxWidth: "400px" }}>
            <Paper className="p-15 bs-primaryMD" style={{ position: "relative" }}>
              <div style={{ position: "absolute", top: 12, right: 12 }}>
                <div className="bg-primary rounded-lg">
                  <LanguageSwitcher />
                </div>
              </div>
              {enrollment ? (
                <TwoFactorEnroll
                  enrollment={enrollment}
                  onEnrolled={completeSignIn}
                  onCancel={restart}
                />
              ) : challenge ? (
                <TwoFactorVerify
                  challenge={challenge}
                  onVerified={completeSignIn}
                  onCancel={restart}
                />
              ) : (
              <Form noValidate onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <div className="tx-center mb-10 mt-20 d-flex j-center">
                      <div
                        className="w-fit p-5"
                        style={{
                          backgroundColor: "var(--primaryXS-color)",
                          borderRadius: "50%",
                        }}
                      >
                        <PersonRounded
                          className="primary-color"
                          sx={{ fontSize: "80px" }}
                        />
                      </div>
                    </div>
                  </Grid>
                  <Grid item xs={12} className="tx-center">
                    <Typography color="error">{errMsg}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Input
                      label={t("username")}
                      name="username"
                      size="medium"
                      value={values.username}
                      onChange={handleInpChg}
                      error={errors.username}
                      className="input-primary w-100pr"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <InputPass
                      label={t("password")}
                      name="password"
                      value={values.password}
                      onChange={handleInpChg}
                      error={errors.password}
                      className="input-primary w-100pr"
                      size="large"
                    />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    className="tx-right"
                    sx={{ pt: "15px !important", pb: "15px", fontSize: "15px" }}
                  >
                    <Link
                      to="forgot"
                      className="gray-color tx-und-none tx-upp hove-primary"
                    >
                      {t("forgotPassword")}
                    </Link>
                  </Grid>
                  <Grid item xs={12} className="text-center">
                    <Button
                      loading={loading}
                      type="submit"
                      fullWidth
                      size="large"
                    >
                      {t("login")}
                    </Button>
                  </Grid>
                </Grid>
              </Form>
              )}
            </Paper>
          </div>
        </div>
      </Container>
    </Grow>
  );
};

export default Login;
